/**
 * Privacy Policy Page Component
 * Comprehensive privacy policy for Lumi AI Figure Skating Training Assistant
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import './LegalPage.css';

function PrivacyPolicy() {
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle anchor link clicks for table of contents
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="legal-page">
      {/* SEO Meta Tags */}
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for LumiSkating AI Figure Skating Assistant. Learn how we collect, use, and protect your personal data."
        url="https://www.lumiskating.com/privacy"
      />

      {/* Header */}
      <header className="legal-header">
        <div className="legal-nav">
          <Link to="/" className="legal-brand">
            <img src="/skate.png" alt="LumiSkating Logo" width="32" height="32" />
            <span>Lumi<span className="brand-skating">Skating</span></span>
          </Link>
          <button className="back-button" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="legal-content">
        <div className="legal-container">
          {/* Title Section */}
          <div className="legal-title-section">
            <h1>Privacy Policy</h1>
            <p className="legal-effective-date">
              <strong>Effective Date:</strong> January 4, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="legal-intro">
            <p>
              At Lumi ("we," "our," or "us"), we understand that your privacy is important.
              This Privacy Policy explains how we collect, use, disclose, and protect your
              personal information when you use our AI-powered figure skating training assistant
              and related services (the "Service").
            </p>
            <p>
              By using our Service, you consent to the practices described in this Privacy Policy.
              If you do not agree with our practices, please do not use the Service.
            </p>
          </div>

          {/* Table of Contents */}
          <nav className="legal-toc">
            <h2>Table of Contents</h2>
            <ol>
              <li><a href="#information-collected" onClick={(e) => scrollToSection(e, 'information-collected')}>Information We Collect</a></li>
              <li><a href="#how-we-use" onClick={(e) => scrollToSection(e, 'how-we-use')}>How We Use Your Information</a></li>
              <li><a href="#video-processing" onClick={(e) => scrollToSection(e, 'video-processing')}>Video Processing and AI Analysis</a></li>
              <li><a href="#data-sharing" onClick={(e) => scrollToSection(e, 'data-sharing')}>How We Share Your Information</a></li>
              <li><a href="#data-retention" onClick={(e) => scrollToSection(e, 'data-retention')}>Data Retention</a></li>
              <li><a href="#data-security" onClick={(e) => scrollToSection(e, 'data-security')}>Data Security</a></li>
              <li><a href="#your-rights" onClick={(e) => scrollToSection(e, 'your-rights')}>Your Privacy Rights</a></li>
              <li><a href="#childrens-privacy" onClick={(e) => scrollToSection(e, 'childrens-privacy')}>Children's Privacy</a></li>
              <li><a href="#cookies" onClick={(e) => scrollToSection(e, 'cookies')}>Cookies and Tracking Technologies</a></li>
              <li><a href="#international" onClick={(e) => scrollToSection(e, 'international')}>International Data Transfers</a></li>
              <li><a href="#third-party" onClick={(e) => scrollToSection(e, 'third-party')}>Third-Party Services</a></li>
              <li><a href="#changes" onClick={(e) => scrollToSection(e, 'changes')}>Changes to This Policy</a></li>
              <li><a href="#contact" onClick={(e) => scrollToSection(e, 'contact')}>Contact Us</a></li>
            </ol>
          </nav>

          {/* Privacy Policy Content */}
          <div className="legal-body">
            <section id="information-collected">
              <h2>1. Information We Collect</h2>
              <p>We collect several types of information to provide and improve our Service:</p>

              <h3>1.1 Information You Provide Directly</h3>
              <ul>
                <li>
                  <strong>Account Information:</strong> When you create an account, we collect your name,
                  email address, password (encrypted), and optionally your competition level.
                </li>
                <li>
                  <strong>Profile Information:</strong> Information you add to your profile, such as
                  skating goals, training history, and preferences.
                </li>
                <li>
                  <strong>Video Content:</strong> Skating videos you upload for analysis, including
                  any metadata associated with those videos.
                </li>
                <li>
                  <strong>Communications:</strong> Messages you send through the chat interface,
                  coaching conversations, and any feedback or support requests.
                </li>
                <li>
                  <strong>Training Data:</strong> Information about your training sessions, goals,
                  schedules, and progress that you input into the Service.
                </li>
              </ul>

              <h3>1.2 Information Collected Automatically</h3>
              <ul>
                <li>
                  <strong>Usage Data:</strong> Information about how you interact with the Service,
                  including features used, time spent, and actions taken.
                </li>
                <li>
                  <strong>Device Information:</strong> Device type, operating system, browser type,
                  screen resolution, and unique device identifiers.
                </li>
                <li>
                  <strong>Log Data:</strong> Server logs including IP address, access times, pages
                  viewed, and referring URLs.
                </li>
                <li>
                  <strong>Performance Data:</strong> Information about app crashes, errors, and
                  performance metrics.
                </li>
              </ul>

              <h3>1.3 Information from AI Analysis</h3>
              <ul>
                <li>
                  <strong>Pose Detection Data:</strong> Body landmark positions and movements extracted
                  from your videos using MediaPipe technology.
                </li>
                <li>
                  <strong>Biomechanical Metrics:</strong> Calculated measurements such as jump height,
                  rotation speed, body angles, and center of mass tracking.
                </li>
                <li>
                  <strong>Performance Analysis:</strong> AI-generated assessments, coaching feedback,
                  and technique evaluations.
                </li>
              </ul>
            </section>

            <section id="how-we-use">
              <h2>2. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>

              <h3>2.1 Providing the Service</h3>
              <ul>
                <li>Process and analyze your skating videos</li>
                <li>Generate personalized coaching feedback and recommendations</li>
                <li>Create and manage your training plans and schedules</li>
                <li>Track your progress and maintain your training history</li>
                <li>Provide the AI chat coaching functionality</li>
              </ul>

              <h3>2.2 Improving the Service</h3>
              <ul>
                <li>Train and improve our AI models using anonymized and aggregated data</li>
                <li>Analyze usage patterns to enhance features and user experience</li>
                <li>Identify and fix technical issues</li>
                <li>Develop new features and capabilities</li>
              </ul>

              <h3>2.3 Communication</h3>
              <ul>
                <li>Send service-related notifications and updates</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Send promotional communications (with your consent)</li>
              </ul>

              <h3>2.4 Safety and Security</h3>
              <ul>
                <li>Identify potential injury risks through our safety monitoring features</li>
                <li>Detect and prevent fraudulent or unauthorized activity</li>
                <li>Enforce our Terms of Service</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section id="video-processing">
              <h2>3. Video Processing and AI Analysis</h2>
              <p className="highlight-box">
                <strong>Important:</strong> Understanding how we process your skating videos is
                essential to your privacy.
              </p>

              <h3>3.1 How Videos Are Processed</h3>
              <p>When you upload a video:</p>
              <ol>
                <li>
                  <strong>Upload and Storage:</strong> Your video is securely uploaded and stored
                  on our servers.
                </li>
                <li>
                  <strong>Pose Detection:</strong> MediaPipe technology analyzes each frame to detect
                  33 body landmarks and track your movement.
                </li>
                <li>
                  <strong>Biomechanical Analysis:</strong> We calculate metrics such as jump height,
                  rotation speed, body angles, and movement patterns.
                </li>
                <li>
                  <strong>AI Coaching:</strong> Our AI (Google Gemini) generates personalized feedback
                  based on the analysis.
                </li>
                <li>
                  <strong>Results Storage:</strong> Analysis results, pose data, and coaching feedback
                  are stored and associated with your account.
                </li>
              </ol>

              <h3>3.2 AI Model Training</h3>
              <p>
                To improve our AI models, we may use anonymized and aggregated data derived from
                video analyses. This data:
              </p>
              <ul>
                <li>Does not include your original videos</li>
                <li>Cannot be traced back to you individually</li>
                <li>Is used solely to improve the accuracy and quality of our analysis</li>
              </ul>

              <h3>3.3 Third-Party AI Services</h3>
              <p>
                We use Google Gemini for AI-powered coaching responses. When generating coaching
                feedback, conversation context may be sent to Google's AI services. Google's
                privacy practices are governed by their own privacy policy.
              </p>
            </section>

            <section id="data-sharing">
              <h2>4. How We Share Your Information</h2>
              <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

              <h3>4.1 Service Providers</h3>
              <p>We work with trusted third-party service providers who assist us in operating the Service:</p>
              <ul>
                <li>
                  <strong>Cloud Hosting:</strong> For secure storage of your data and videos
                </li>
                <li>
                  <strong>AI Processing:</strong> Google Gemini for generating coaching feedback
                </li>
                <li>
                  <strong>Analytics:</strong> To understand usage patterns and improve the Service
                </li>
                <li>
                  <strong>Payment Processing:</strong> For subscription payments (if applicable)
                </li>
              </ul>
              <p>
                These providers are contractually bound to protect your information and may only
                use it to provide services on our behalf.
              </p>

              <h3>4.2 Legal Requirements</h3>
              <p>We may disclose your information if required by law, such as:</p>
              <ul>
                <li>To comply with legal process or government requests</li>
                <li>To protect our rights, privacy, safety, or property</li>
                <li>To enforce our Terms of Service</li>
                <li>In connection with a legal investigation</li>
              </ul>

              <h3>4.3 Business Transfers</h3>
              <p>
                If Lumi is involved in a merger, acquisition, or sale of assets, your information
                may be transferred as part of that transaction. We will notify you of any change
                in ownership or control of your personal information.
              </p>

              <h3>4.4 With Your Consent</h3>
              <p>
                We may share your information for other purposes if you have given us explicit
                consent to do so.
              </p>
            </section>

            <section id="data-retention">
              <h2>5. Data Retention</h2>
              <p>We retain your information for as long as necessary to provide the Service and fulfill the purposes described in this policy:</p>

              <h3>5.1 Active Accounts</h3>
              <ul>
                <li>
                  <strong>Account Information:</strong> Retained while your account is active
                </li>
                <li>
                  <strong>Videos:</strong> Retained until you delete them or close your account
                </li>
                <li>
                  <strong>Analysis Data:</strong> Retained with associated videos for your reference
                </li>
                <li>
                  <strong>Conversation History:</strong> Retained to provide context for ongoing coaching
                </li>
              </ul>

              <h3>5.2 After Account Deletion</h3>
              <p>When you delete your account:</p>
              <ul>
                <li>Your personal information is deleted within 30 days</li>
                <li>Your videos are permanently deleted within 30 days</li>
                <li>Anonymized, aggregated data used for AI training may be retained</li>
                <li>Backup copies may persist for up to 90 days before deletion</li>
              </ul>

              <h3>5.3 Legal Retention</h3>
              <p>
                We may retain certain information longer if required by law or for legitimate
                business purposes such as fraud prevention or dispute resolution.
              </p>
            </section>

            <section id="data-security">
              <h2>6. Data Security</h2>
              <p>
                We implement robust security measures to protect your information:
              </p>
              <ul>
                <li>
                  <strong>Encryption:</strong> Data is encrypted in transit (TLS/SSL) and at rest
                </li>
                <li>
                  <strong>Access Controls:</strong> Strict access controls limit who can access your data
                </li>
                <li>
                  <strong>Secure Authentication:</strong> Passwords are hashed using industry-standard algorithms
                </li>
                <li>
                  <strong>Regular Audits:</strong> We conduct security assessments and monitoring
                </li>
                <li>
                  <strong>Incident Response:</strong> We have procedures to handle security incidents
                </li>
              </ul>
              <p>
                While we strive to protect your information, no method of transmission or storage
                is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section id="your-rights">
              <h2>7. Your Privacy Rights</h2>
              <p>
                Depending on your location, you may have certain rights regarding your personal information:
              </p>

              <h3>7.1 Access and Portability</h3>
              <p>
                You can access your personal information through your account settings. You may
                request a copy of your data in a portable format.
              </p>

              <h3>7.2 Correction</h3>
              <p>
                You can update your account information at any time through your settings.
                Contact us for corrections to other data.
              </p>

              <h3>7.3 Deletion</h3>
              <p>
                You can delete your account and request deletion of your personal information.
                Some information may be retained as described in our data retention section.
              </p>

              <h3>7.4 Objection and Restriction</h3>
              <p>
                You may object to certain processing of your data or request that we restrict
                processing in certain circumstances.
              </p>

              <h3>7.5 Marketing Opt-Out</h3>
              <p>
                You can opt out of marketing communications at any time by clicking the
                unsubscribe link or adjusting your notification settings.
              </p>

              <h3>7.6 Exercising Your Rights</h3>
              <p>
                To exercise these rights, contact us at privacy@lumiskating.com. We will respond
                to your request within the timeframe required by applicable law.
              </p>

              <h3>7.7 Regional Rights</h3>
              <p>
                <strong>California Residents (CCPA):</strong> You have the right to know what
                personal information we collect, request deletion, and opt out of the sale of
                personal information. We do not sell personal information.
              </p>
              <p>
                <strong>EU/EEA Residents (GDPR):</strong> You have additional rights including
                the right to lodge a complaint with your local data protection authority.
              </p>
            </section>

            <section id="childrens-privacy">
              <h2>8. Children's Privacy</h2>
              <p className="highlight-box">
                <strong>Important for Parents and Guardians:</strong> Please read this section
                if your child uses our Service.
              </p>

              <h3>8.1 Age Requirements</h3>
              <p>
                Our Service is not intended for children under 13 years of age. Users between
                13 and 18 must have parental or guardian consent to use the Service.
              </p>

              <h3>8.2 Parental Consent</h3>
              <p>
                If a child under 18 uses our Service, the parent or guardian who provides consent:
              </p>
              <ul>
                <li>Agrees to these privacy practices on behalf of the child</li>
                <li>Is responsible for monitoring the child's use of the Service</li>
                <li>May review, request deletion of, or restrict the child's information</li>
              </ul>

              <h3>8.3 Parental Rights</h3>
              <p>
                Parents and guardians may:
              </p>
              <ul>
                <li>Request access to their child's personal information</li>
                <li>Request deletion of their child's account and data</li>
                <li>Withdraw consent for continued data collection</li>
              </ul>
              <p>
                To exercise these rights, contact us at privacy@lumiskating.com with proof
                of guardianship.
              </p>
            </section>

            <section id="cookies">
              <h2>9. Cookies and Tracking Technologies</h2>

              <h3>9.1 What We Use</h3>
              <p>We use the following technologies:</p>
              <ul>
                <li>
                  <strong>Essential Cookies:</strong> Required for the Service to function
                  (authentication, security, preferences)
                </li>
                <li>
                  <strong>Analytics:</strong> To understand how the Service is used
                </li>
                <li>
                  <strong>Local Storage:</strong> To store preferences and cached data
                </li>
              </ul>

              <h3>9.2 Your Choices</h3>
              <p>
                You can control cookies through your browser settings. Disabling certain
                cookies may affect the functionality of the Service.
              </p>

              <h3>9.3 Do Not Track</h3>
              <p>
                We currently do not respond to "Do Not Track" signals. We treat all users
                according to the practices described in this policy.
              </p>
            </section>

            <section id="international">
              <h2>10. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than
                your country of residence. These countries may have different data protection
                laws than your country.
              </p>
              <p>
                When we transfer data internationally, we implement appropriate safeguards
                to protect your information, including:
              </p>
              <ul>
                <li>Standard contractual clauses approved by relevant authorities</li>
                <li>Ensuring service providers meet adequate protection standards</li>
                <li>Implementing technical and organizational security measures</li>
              </ul>
            </section>

            <section id="third-party">
              <h2>11. Third-Party Services</h2>
              <p>
                Our Service may contain links to third-party websites or integrate with
                third-party services. This Privacy Policy does not apply to those third parties.
                We encourage you to review the privacy policies of any third-party services you use.
              </p>

              <h3>Key Third-Party Services</h3>
              <ul>
                <li>
                  <strong>Google Gemini:</strong> AI model for generating coaching feedback
                  (Google Privacy Policy applies)
                </li>
                <li>
                  <strong>MediaPipe:</strong> Pose detection technology (processed locally/on our servers)
                </li>
              </ul>
            </section>

            <section id="changes">
              <h2>12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our
                practices or for legal, regulatory, or operational reasons.
              </p>
              <p>
                When we make changes:
              </p>
              <ul>
                <li>We will update the "Effective Date" at the top of this policy</li>
                <li>For material changes, we will notify you by email or through the Service</li>
                <li>We may ask for your consent if required by law</li>
              </ul>
              <p>
                Your continued use of the Service after changes take effect constitutes your
                acceptance of the updated policy.
              </p>
            </section>

            <section id="contact">
              <h2>13. Contact Us</h2>
              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or
                our data practices, please contact us:
              </p>
              <div className="contact-info">
                <p><strong>Privacy Inquiries:</strong> privacy@lumiskating.com</p>
                <p><strong>Data Protection Officer:</strong> dpo@lumiskating.com</p>
                <p><strong>General Support:</strong> support@lumiskating.com</p>
                <p><strong>Address:</strong> Lumi Figure Skating Training</p>
              </div>
              <p>
                We aim to respond to all privacy-related inquiries within 30 days.
              </p>
            </section>

            {/* Acknowledgment */}
            <section className="acknowledgment">
              <p>
                By using the Lumi Service, you acknowledge that you have read and understood
                this Privacy Policy and consent to the collection, use, and sharing of your
                information as described herein.
              </p>
            </section>
          </div>

          {/* Related Links */}
          <div className="legal-related">
            <h3>Related Documents</h3>
            <div className="related-links">
              <Link to="/terms" className="related-link">
                <i className="fas fa-file-contract"></i>
                <span>Terms of Service</span>
              </Link>
              <Link to="/" className="related-link">
                <i className="fas fa-home"></i>
                <span>Return to Home</span>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>&copy; 2026 LumiSkating. All rights reserved.</p>
          <div className="legal-footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
